import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { injectIntl, useIntl } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import DataCacheService from '@/shared/services/DataCacheService';
import MultiSelect, { addItem, removeItem, splitItems } from './MultiSelect';

/**
 * Mapper used to map object from the datacache to items ({id,label})
 *
 * @callback dataCacheMultiSelectMapper
 * @param {Object} dataCache The dataCache object from the DataCacheService
 * @param {*} intl
 * @param {boolean} [showWithoutOption=false]
 * @returns Array of {id,label}
 */

/**
 * Create DataCacheMultiSelect using mapper
 *
 * @param {dataCacheMultiSelectMapper} mapper The mapper
 * @param {Object} [placeholderMessage] The intl message for the placeholder
 */
const dataCacheMultiSelectHoc = (mapper, placeholderMessage = null) => {
  if (typeof mapper !== 'function') {
    console.error('Invalid dataCacheMultiSelect mapper');
    return null;
  }
  const DataCacheMultiSelect = ({ selection, onChange, intl, placeholder, showWithoutOption }) => {
    const [items, setItems] = useState([]);

    useEffect(() => {
      const dataCache = DataCacheService.getDataCache();
      if (!dataCache) return;

      setItems(mapper(dataCache, intl, showWithoutOption));
    }, [intl]);

    const [selectedItems, selectableItems] = splitItems(items, selection);

    return (
      <MultiSelect
        selectedItem={selectedItems}
        onItemRemoved={item => onChange(removeItem(selection, item))}
        onItemSelected={item => onChange(addItem(selection, item))}
        items={selectableItems}
        placeholder={
          placeholder ||
          (placeholderMessage && placeholderMessage.id
            ? intl.formatMessage(placeholderMessage)
            : null)
        }
      />
    );
  };
  DataCacheMultiSelect.propTypes = {
    selection: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    showWithoutOption: PropTypes.bool,
    intl: intlShape.isRequired,
  };
  DataCacheMultiSelect.defaultProps = {
    selection: [],
    placeholder: null,
    showWithoutOption: false,
  };

  return injectIntl(DataCacheMultiSelect);
};
export default dataCacheMultiSelectHoc;
